import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Upload, AlertTriangle, Check, ChevronDown, ChevronRight, Package, Users, Truck, Wallet, Receipt } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const fmtRs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const Section = ({ title, icon: Icon, color, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden mb-4" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#f9fafb] transition-colors">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${color}`}><Icon className="w-4 h-4 text-white" /></div>
        <span className="flex-1 text-left font-semibold text-[#1a1a2e]">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-[#6b7280]" /> : <ChevronRight className="w-4 h-4 text-[#6b7280]" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#e5e7eb]">{children}</div>}
    </div>
  );
};

const PreviewTable = ({ headers, rows }) => (
  <div className="overflow-x-auto mt-3 mb-3 max-h-60 overflow-y-auto border border-[#e5e7eb] rounded-xl">
    <table className="w-full text-xs">
      <thead className="bg-[#f9fafb] text-[#6b7280] sticky top-0">
        <tr>{headers.map((h, i) => <th key={i} className="py-2 px-3 text-left font-medium">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-[#e5e7eb]">
            {headers.map((h, j) => <td key={j} className="py-2 px-3 text-[#1a1a2e]">{row[h.toLowerCase().replace(/[^a-z_]/g, '_')] ?? row[Object.keys(row)[j]] ?? ''}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OpeningBalances = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [importDone, setImportDone] = useState(false);

  // Section states: preview data, loading, result
  const [productsPreview, setProductsPreview] = useState(null);
  const [customersPreview, setCustomersPreview] = useState(null);
  const [suppliersPreview, setSuppliersPreview] = useState(null);
  const [expensesPreview, setExpensesPreview] = useState(null);
  const [productsFile, setProductsFile] = useState(null);
  const [customersFile, setCustomersFile] = useState(null);
  const [suppliersFile, setSuppliersFile] = useState(null);
  const [expensesFile, setExpensesFile] = useState(null);
  const [cashBalance, setCashBalance] = useState('');
  const [bankBalance, setBankBalance] = useState('');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [results, setResults] = useState({});
  const [loadingSection, setLoadingSection] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    const done = localStorage.getItem('ob_import_done');
    if (done === 'true') setImportDone(true);
  }, [navigate]);

  const downloadTemplate = async (type) => {
    try {
      const r = await api.get(`/opening-balances/template/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to download template');
    }
  };

  const uploadPreview = async (type, file, setPreview, setFile) => {
    if (!file) return;
    setFile(file);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(`/opening-balances/preview/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to parse file');
    }
  };

  const confirmImport = async (type, file) => {
    if (!file) return;
    setLoadingSection(type);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(`/opening-balances/confirm/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResults(prev => ({ ...prev, [type]: r.data }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Import failed');
    }
    setLoadingSection('');
  };

  const confirmCashBank = async () => {
    const cash = parseFloat(cashBalance) || 0;
    const bank = parseFloat(bankBalance) || 0;
    if (cash <= 0 && bank <= 0) { alert('Enter at least one balance.'); return; }
    setLoadingSection('cash-bank');
    try {
      const r = await api.post('/opening-balances/confirm/cash-bank', { cash_balance: cash, bank_balance: bank, as_of_date: asOfDate });
      setResults(prev => ({ ...prev, 'cash-bank': r.data }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    }
    setLoadingSection('');
  };

  const markDone = () => {
    setImportDone(true);
    localStorage.setItem('ob_import_done', 'true');
  };

  const ResultBadge = ({ type }) => {
    const r = results[type];
    if (!r) return null;
    return <div className="mt-3 bg-[#DBEAFE] border border-[#93C5FD] rounded-xl px-4 py-2 text-sm text-[#1E40AF] flex items-center gap-2"><Check className="w-4 h-4" />{r.message}</div>;
  };

  if (importDone) {
    return (
      <div className="flex h-screen bg-[#f0f2f5]" data-testid="opening-balances-done">
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
          <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-6 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1a1a2e] mb-6">Opening Balances Import</h1>
            <div className="bg-[#DBEAFE] border border-[#93C5FD] rounded-2xl p-6 text-center">
              <Check className="w-10 h-10 mx-auto text-[#1E40AF] mb-3" />
              <p className="text-lg font-semibold text-[#1E40AF]">Import Already Done</p>
              <p className="text-sm text-[#6b7280] mt-2">Opening balances have been imported. No further imports needed.</p>
              <button onClick={() => { setImportDone(false); localStorage.removeItem('ob_import_done'); }}
                className="mt-4 text-xs text-[#6b7280] underline" data-testid="reset-import-btn">Reset (for testing)</button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="opening-balances-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1a1a2e] mb-4" data-testid="opening-balances-title">Opening Balances Import</h1>

          {/* Warning Banner */}
          <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 rounded-sm mb-6 flex items-start gap-3" data-testid="warning-banner">
            <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-[#92400E]">Opening balances should only be imported once when you first start using SAMS.</p>
              <p className="text-xs text-[#92400E] mt-1">Importing again will create duplicate entries.</p>
            </div>
          </div>

          {/* Section 1: Products */}
          <Section title="Products & Opening Stock" icon={Package} color="bg-[#1E40AF]" testId="section-products">
            <div className="pt-4 space-y-3">
              <div className="flex gap-3">
                <button onClick={() => downloadTemplate('products')} className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb]" data-testid="download-products-template">
                  <Download className="w-4 h-4" />Download Template
                </button>
                <label className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb] cursor-pointer" data-testid="upload-products-btn">
                  <Upload className="w-4 h-4" />Upload Excel
                  <input type="file" accept=".xlsx" className="hidden" onChange={e => uploadPreview('products', e.target.files[0], setProductsPreview, setProductsFile)} />
                </label>
              </div>
              {productsPreview && (
                <>
                  <p className="text-sm text-[#6b7280]">{productsPreview.count} products found in file:</p>
                  <PreviewTable headers={['Name', 'Category', 'Unit', 'Quantity', 'Cost', 'Selling', 'Reorder']}
                    rows={productsPreview.rows.map(r => ({ Name: r.name, Category: r.category, Unit: r.unit, Quantity: r.quantity, Cost: fmtRs(r.cost_price), Selling: fmtRs(r.selling_price), Reorder: r.reorder_level }))} />
                  <button onClick={() => confirmImport('products', productsFile)} disabled={loadingSection === 'products'}
                    className="flex items-center gap-2 bg-[#1E40AF] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50" data-testid="confirm-products-btn">
                    <Check className="w-4 h-4" />{loadingSection === 'products' ? 'Importing...' : 'Confirm Import'}
                  </button>
                </>
              )}
              <ResultBadge type="products" />
            </div>
          </Section>

          {/* Section 2: Customers */}
          <Section title="Customer Opening Balances" icon={Users} color="bg-[#1E40AF]" testId="section-customers">
            <div className="pt-4 space-y-3">
              <div className="flex gap-3">
                <button onClick={() => downloadTemplate('customers')} className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb]" data-testid="download-customers-template"><Download className="w-4 h-4" />Download Template</button>
                <label className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb] cursor-pointer" data-testid="upload-customers-btn"><Upload className="w-4 h-4" />Upload Excel
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => uploadPreview('customers', e.target.files[0], setCustomersPreview, setCustomersFile)} />
                </label>
              </div>
              {customersPreview && (
                <>
                  <p className="text-sm text-[#6b7280]">{customersPreview.count} customers found:</p>
                  <PreviewTable headers={['Name', 'Phone', 'City', 'Balance']}
                    rows={customersPreview.rows.map(r => ({ Name: r.name, Phone: r.phone, City: r.city, Balance: fmtRs(r.balance) }))} />
                  <button onClick={() => confirmImport('customers', customersFile)} disabled={loadingSection === 'customers'}
                    className="flex items-center gap-2 bg-[#1E40AF] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50" data-testid="confirm-customers-btn"><Check className="w-4 h-4" />{loadingSection === 'customers' ? 'Importing...' : 'Confirm Import'}</button>
                </>
              )}
              <ResultBadge type="customers" />
            </div>
          </Section>

          {/* Section 3: Suppliers */}
          <Section title="Supplier Opening Balances" icon={Truck} color="bg-[#7C3AED]" testId="section-suppliers">
            <div className="pt-4 space-y-3">
              <div className="flex gap-3">
                <button onClick={() => downloadTemplate('suppliers')} className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb]" data-testid="download-suppliers-template"><Download className="w-4 h-4" />Download Template</button>
                <label className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb] cursor-pointer" data-testid="upload-suppliers-btn"><Upload className="w-4 h-4" />Upload Excel
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => uploadPreview('suppliers', e.target.files[0], setSuppliersPreview, setSuppliersFile)} />
                </label>
              </div>
              {suppliersPreview && (
                <>
                  <p className="text-sm text-[#6b7280]">{suppliersPreview.count} suppliers found:</p>
                  <PreviewTable headers={['Name', 'Phone', 'City', 'Balance']}
                    rows={suppliersPreview.rows.map(r => ({ Name: r.name, Phone: r.phone, City: r.city, Balance: fmtRs(r.balance) }))} />
                  <button onClick={() => confirmImport('suppliers', suppliersFile)} disabled={loadingSection === 'suppliers'}
                    className="flex items-center gap-2 bg-[#1E40AF] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50" data-testid="confirm-suppliers-btn"><Check className="w-4 h-4" />{loadingSection === 'suppliers' ? 'Importing...' : 'Confirm Import'}</button>
                </>
              )}
              <ResultBadge type="suppliers" />
            </div>
          </Section>

          {/* Section 4: Cash & Bank */}
          <Section title="Bank & Cash Opening Balances" icon={Wallet} color="bg-[#4A7AE0]" testId="section-cash-bank">
            <div className="pt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">Cash in Hand</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#6b7280]">Rs.</span>
                    <input type="number" min="0" value={cashBalance} onChange={e => setCashBalance(e.target.value)}
                      className="w-full border border-[#e5e7eb] rounded-xl pl-10 pr-3 py-2 text-sm" data-testid="cash-balance-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">Bank Account</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#6b7280]">Rs.</span>
                    <input type="number" min="0" value={bankBalance} onChange={e => setBankBalance(e.target.value)}
                      className="w-full border border-[#e5e7eb] rounded-xl pl-10 pr-3 py-2 text-sm" data-testid="bank-balance-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">As of Date</label>
                  <input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)}
                    className="w-full border border-[#e5e7eb] rounded-xl px-3 py-2 text-sm" data-testid="cash-bank-date" />
                </div>
              </div>
              <button onClick={confirmCashBank} disabled={loadingSection === 'cash-bank'}
                className="flex items-center gap-2 bg-[#1E40AF] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50" data-testid="save-cash-bank-btn">
                <Check className="w-4 h-4" />{loadingSection === 'cash-bank' ? 'Saving...' : 'Save'}
              </button>
              <ResultBadge type="cash-bank" />
            </div>
          </Section>

          {/* Section 5: Opening Expenses */}
          <Section title="Opening Expenses" icon={Receipt} color="bg-[#DC2626]" testId="section-expenses">
            <div className="pt-4 space-y-3">
              <div className="flex gap-3">
                <button onClick={() => downloadTemplate('expenses')} className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb]" data-testid="download-expenses-template"><Download className="w-4 h-4" />Download Template</button>
                <label className="flex items-center gap-2 border border-[#e5e7eb] bg-white px-4 py-2 rounded-xl text-sm hover:bg-[#f9fafb] cursor-pointer" data-testid="upload-expenses-btn"><Upload className="w-4 h-4" />Upload Excel
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => uploadPreview('expenses', e.target.files[0], setExpensesPreview, setExpensesFile)} />
                </label>
              </div>
              {expensesPreview && (
                <>
                  <p className="text-sm text-[#6b7280]">{expensesPreview.count} expenses found:</p>
                  <PreviewTable headers={['Date', 'Category', 'Description', 'Amount', 'Payment']}
                    rows={expensesPreview.rows.map(r => ({ Date: r.date, Category: r.category, Description: r.description, Amount: fmtRs(r.amount), Payment: r.payment_method }))} />
                  <button onClick={() => confirmImport('expenses', expensesFile)} disabled={loadingSection === 'expenses'}
                    className="flex items-center gap-2 bg-[#1E40AF] text-white px-5 py-2 rounded-xl text-sm disabled:opacity-50" data-testid="confirm-expenses-btn"><Check className="w-4 h-4" />{loadingSection === 'expenses' ? 'Importing...' : 'Confirm Import'}</button>
                </>
              )}
              <ResultBadge type="expenses" />
            </div>
          </Section>

          {/* Mark Import Done */}
          <div className="mt-6 text-center">
            <button onClick={markDone} className="bg-[#1a1a2e] text-white px-8 py-3 rounded-xl text-sm font-semibold hover:bg-[#2d2d44] transition-colors" data-testid="mark-import-done-btn">
              Import Complete — Mark as Done
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default OpeningBalances;
